import { Trip } from "@/state/trips"
import { milesToMeters } from "@/state/reportv2/convert"
import { filterDuplicates } from "../utils/array"
import {
	addressString,
	coords,
	OverpassAddress,
	ShortAddress,
} from "../maps/utils/address"
import { group } from "../maps/utils/group"
import { clusterize } from "../maps/utils/clusterize"
import { AddressType } from "@/state/draft/hqAddress"
import { requestDistanceMatrix } from "../maps/utils/distance-matrix"
import { runAlgoClusters } from "../maps/utils/trips"
import { flattenResults } from "../maps/utils/prettify"
import { loadOverpassData } from "../maps/utils/overpass"
import { ALGO_CONFIG } from "@/config"
import { ProfessionId } from "@/constants/professions"
import { getProfessionConfig } from "@/constants/getConfig"
import { PlainDate, PlainDateTime } from "@/utils/date"
import { Temporal } from "temporal-polyfill"

export const restoreAddressesWithAi = async (
	startPoint: AddressType,
	count: number,
	radiusMiles: number
) => {
	const addresses = await generateAddresses(
		startPoint,
		milesToMeters(radiusMiles),
		count
	)
	return addresses
}

const uniqueAddressesDuringPeriodCoef = (periodMonths: number) =>
	0.3 + (0.7 / 11) * (periodMonths - 1)

export const uniqueAddressesDuringPeriod = (
	requestedAddressessCount: number,
	periodMonths: number
) => Math.round(requestedAddressessCount * uniqueAddressesDuringPeriodCoef(periodMonths))

/**
 * @returns radius in meters
 */
export const radiusForProfession = (professionId: ProfessionId) => {
	const config = getProfessionConfig(professionId)
	if (!config) {
		console.error(`[radiusForProfession] no config for professionId: ${professionId}`)
		return 5000
	}
	return milesToMeters(config.radiusMiles)
}

export const addressesCountForProfession = (professionId: ProfessionId) => {
	const config = getProfessionConfig(professionId)
	if (!config) {
		console.error(
			`[addressesCountForProfession] no config for professionId: ${professionId}`
		)
		return 1000
	}
	return config.addressesPerYear.max
}

export const generateAddresses = async (
	startPoint: AddressType,
	radiusMeters: number,
	limitResult: number,
	loadOverpassDataReq = loadOverpassData
): Promise<ShortAddress[]> => {
	// step 1
	let ADDRESSES = await loadOverpassDataReq({
		type: "each",
		coords: startPoint.latlon,
		radiusMeters: radiusMeters,
		limitResults: limitResult,
		timeout: 50,
	}).then((x) => x.elements)

	return filterAddresses(ADDRESSES)
}
export function filterAddresses(ADDRESSES: OverpassAddress[]) {
	// step 2
	const addresses = ADDRESSES.filter((x) => x.tags["addr:street"]) // remove empty streets
		.map((x) => ({
			latlon: coords(x),
			address: addressString(x.tags),
			name: x.tags.name,
		}))

	// step 3
	return filterDuplicates(addresses, (x) => x.address)
}

export const onboardAlgo = async (
	args: {
		totalMiles: number
		workingDays: readonly PlainDate[]
		startAddress: AddressType
		dailyTimeLimit: number
		timeSpentAtLocation: number
		timeSpentAtOffice: number
		addresses: readonly ShortAddress[]
		startTime: string
	},
	requestDistanceMatrixF = requestDistanceMatrix
): Promise<Trip[]> => {
	let addresses = args.addresses
	console.log(`[ONBOARD] addresses: ${addresses.length}`)

	// step 4
	// addresses.unshift(args.startAddress)
	const groups = group(addresses, ALGO_CONFIG.GROUP_RADIUS_DEFAULT)
	console.log(`[ONBOARD] groups: ${groups.length}`)
	// step 5 clusterize
	const maxClusters = 3
	const clusters = clusterize(groups, maxClusters) //.filter((x) => x?.length > 1)
	console.log(`[ONBOARD] clusters: ${clusters.length}`)
	// step 6 request distance matrix
	let arr = []
	for await (const json of requestDistanceMatrixF(clusters)) {
		arr.push(json)
		console.log(`${arr.length}/${clusters.length}`)
	}
	console.log(`[ONBOARD] distances: ${arr.length}`)

	// step 7 trips
	const matrix = arr
	let distances = matrix.map((x) => x.distances)
	const targetSum = milesToMeters(args.totalMiles)

	let timeSpentInNodesArr = distances.map((d) => {
		let arr = Array.from({ length: d.length }, () => args.timeSpentAtLocation) // TODO: calculate random time between min and max as provided by user
		arr[0] = args.timeSpentAtOffice
		return arr
	})

	let res = runAlgoClusters(
		args.workingDays.length,
		distances,
		targetSum,
		args.dailyTimeLimit,
		timeSpentInNodesArr
	)
	let trips = res.results.map((x) => x.paths)
	console.log(`[ONBOARD] days: ${trips.length}`)

	// step 8 prettify

	let startDateTimes = args.workingDays.map((x) =>
		PlainDateTime.fromTemporal(Temporal.PlainTime.from(args.startTime), x)
	)
	let pretty = flattenResults(
		clusters,
		trips,
		res.distances,
		res.times,
		timeSpentInNodesArr,
		startDateTimes
	)
	console.log(`[ONBOARD] pretty: ${pretty.length}`)
	return (pretty[0] || []).map((x) => ({
		date: x.date.date,
		vehicle: x.vehicle,
		startAddress: x.startAddress,
		endAddress: x.endAddress,
		mileage: x.mileage,
		odometer: 0,
		timeStart: x.timeStart.timeMinutes,
	}))
}
